/**
 * SectionCreateModal — UI für `POST /api/v1/posts/{id}/sections`.
 * Whiteboard Doc-Komm-System Phase C2.
 *
 * Drei Section-Types: product / question / task. Jeder Type hat ein
 * eigenes Mini-Formular für die type-spezifischen Attrs:
 *   - product: name, mode, goal
 *   - question: initiator, targets (comma-separated), main-text
 *   - task: assignee, status, due
 *
 * Submit → backend stempelt UUID, fügt <section>…</section> ans Doc-Ende,
 * caller refetched.
 */

import { useState } from 'react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'

type SectionType = 'product' | 'question' | 'task'

interface Props {
  postId: number
  open: boolean
  onClose: () => void
  onCreated?: (sectionId: string, sectionType: SectionType) => void
}

export function SectionCreateModal({ postId, open, onClose, onCreated }: Props) {
  const [type, setType] = useState<SectionType>('question')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // type-shared
  const [bodyText, setBodyText] = useState('')

  // product
  const [pName, setPName] = useState('')
  const [pMode, setPMode] = useState<'improve' | 'draft' | 'review'>('improve')
  const [pGoal, setPGoal] = useState('')

  // question
  const me = getUser()
  const myDisplay = me ? `${me.first_name} ${me.last_name}`.trim() || me.email : ''
  const [qInitiator, setQInitiator] = useState(myDisplay || 'me')
  const [qTargets, setQTargets] = useState('')

  // task
  const [tAssignee, setTAssignee] = useState('')
  const [tStatus, setTStatus] = useState<'open' | 'in_progress' | 'done'>('open')
  const [tDue, setTDue] = useState('')

  const reset = () => {
    setType('question')
    setBodyText('')
    setError(null)
    setPName(''); setPMode('improve'); setPGoal('')
    setQInitiator(myDisplay || 'me'); setQTargets('')
    setTAssignee(''); setTStatus('open'); setTDue('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const submit = async () => {
    const tok = getAccessToken()
    if (!tok) {
      setError('Du bist nicht eingeloggt.')
      return
    }
    let attrs: Record<string, string> = {}
    if (type === 'product') {
      if (!pName.trim()) { setError('Name ist Pflicht.'); return }
      attrs = { name: pName.trim(), mode: pMode }
      if (pGoal.trim()) attrs.goal = pGoal.trim()
    } else if (type === 'question') {
      if (!qInitiator.trim()) { setError('Initiator ist Pflicht.'); return }
      if (!bodyText.trim()) { setError('Frage-Text ist Pflicht.'); return }
      attrs = { initiator: qInitiator.trim(), status: 'open' }
      if (qTargets.trim()) attrs.targets = qTargets.trim()
    } else {
      if (!tAssignee.trim()) { setError('Assignee ist Pflicht.'); return }
      attrs = { assignee: tAssignee.trim(), status: tStatus }
      if (tDue.trim()) attrs.due = tDue.trim()
    }

    setBusy(true)
    setError(null)
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tok}`,
          },
          body: JSON.stringify({
            type,
            attrs,
            initial_text: bodyText.trim() || undefined,
          }),
        },
      )
      if (!r.ok) {
        const txt = await r.text()
        setError(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
        return
      }
      const data = await r.json()
      onCreated?.(data.id, type)
      reset()
      onClose()
    } catch (e) {
      setError((e as Error).message || String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)',
          borderRadius: 12,
          maxWidth: 540,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          padding: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 14,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20 }}>Section hinzufügen</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 22,
              cursor: 'pointer',
              color: 'var(--text-muted, #888)',
            }}
          >
            ×
          </button>
        </div>

        {/* Type Picker */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            marginBottom: 18,
          }}
        >
          <TypeButton active={type === 'product'} onClick={() => setType('product')}>
            🧱 Product
          </TypeButton>
          <TypeButton active={type === 'question'} onClick={() => setType('question')}>
            ❓ Frage
          </TypeButton>
          <TypeButton active={type === 'task'} onClick={() => setType('task')}>
            ✅ Aufgabe
          </TypeButton>
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: '#fee',
              color: '#900',
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Type-specific Fields */}
        {type === 'product' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <input
              placeholder="Name (z.B. Landing-Page Entwurf)"
              value={pName}
              onChange={(e) => setPName(e.target.value)}
              style={inputStyle}
            />
            <select
              value={pMode}
              onChange={(e) => setPMode(e.target.value as 'improve' | 'draft' | 'review')}
              style={inputStyle}
            >
              <option value="improve">improve — schon was, soll besser werden</option>
              <option value="draft">draft — Erstentwurf von null</option>
              <option value="review">review — bestehendes prüfen</option>
            </select>
            <input
              placeholder="Goal (optional, z.B. 'geile Landing-Page')"
              value={pGoal}
              onChange={(e) => setPGoal(e.target.value)}
              style={inputStyle}
            />
            <textarea
              placeholder="Initialer Inhalt (Markdown / Code)"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={6}
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13 }}
            />
          </div>
        )}

        {type === 'question' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <input
              placeholder="Initiator (z.B. dein Name)"
              value={qInitiator}
              onChange={(e) => setQInitiator(e.target.value)}
              style={inputStyle}
            />
            <input
              placeholder="Targets — wen fragst du? (z.B. Cloud,ArkturianClaude)"
              value={qTargets}
              onChange={(e) => setQTargets(e.target.value)}
              style={inputStyle}
            />
            <textarea
              placeholder="Deine Frage"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={5}
              style={inputStyle}
            />
          </div>
        )}

        {type === 'task' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <input
              placeholder="Assignee (z.B. agent:Content oder user:UUID)"
              value={tAssignee}
              onChange={(e) => setTAssignee(e.target.value)}
              style={inputStyle}
            />
            <select
              value={tStatus}
              onChange={(e) =>
                setTStatus(e.target.value as 'open' | 'in_progress' | 'done')
              }
              style={inputStyle}
            >
              <option value="open">open</option>
              <option value="in_progress">in_progress</option>
              <option value="done">done</option>
            </select>
            <input
              type="date"
              placeholder="Due"
              value={tDue}
              onChange={(e) => setTDue(e.target.value)}
              style={inputStyle}
            />
            <textarea
              placeholder="Aufgaben-Beschreibung"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={5}
              style={inputStyle}
            />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            onClick={submit}
            disabled={busy}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: 6,
              border: 0,
              background: 'var(--accent, #4a6cf7)',
              color: 'white',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Lade …' : 'Section anlegen'}
          </button>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 16px',
              borderRadius: 6,
              border: '1px solid var(--border, #ccc)',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}

function TypeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${active ? 'var(--accent, #4a6cf7)' : 'var(--border, #ccc)'}`,
        background: active ? 'rgba(74, 108, 247, 0.08)' : 'white',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--accent, #4a6cf7)' : 'var(--text, #333)',
      }}
    >
      {children}
    </button>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid var(--border, #ccc)',
  fontSize: 14,
  fontFamily: 'inherit',
  resize: 'vertical',
}
